import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Dices, 
  Coffee, 
  User, 
  Users, 
  Sparkles, 
  ArrowRight, 
  Sword,
  Crown,
  Ticket,
  CalendarCheck,
  Percent,
  PercentSquare
} from 'lucide-react';

// --- Shared UI Components ---

const SectionSeparator = () => (
  <div className="flex justify-center items-center py-16 opacity-40">
    <div className="h-[1px] w-24 bg-gradient-to-r from-transparent to-[#971253]"></div>
    <div className="mx-6 text-[#971253]">
      <Crown size={20} strokeWidth={1} />
    </div>
    <div className="h-[1px] w-24 bg-gradient-to-l from-transparent to-[#971253]"></div>
  </div>
);

// --- Subscription Pricing Card Component ---
const PricingCard = ({ duration, totalPrice, monthlyPerPerson, savings, isPremium, hasDiscount, onClick }) => {
  // Lógica para separar a parte inteira dos centavos de forma segura
  const priceString = String(monthlyPerPerson);
  const [integerPart, centsPart] = priceString.includes(',') 
    ? priceString.split(',') 
    : priceString.includes('.') 
        ? priceString.split('.') 
        : [priceString, '00'];

  // Define a porcentagem de desconto baseada na duração do plano
  const discountPercent = (duration === "12" || duration === 12) ? '8' : '5';

  return (
    <div 
      onClick={onClick}
      className={`
      relative overflow-hidden p-6 rounded-2xl transition-all duration-500 cursor-pointer
      flex flex-col items-center text-center border-2 h-full
      hover:-translate-y-1 hover:shadow-xl
      ${isPremium 
        ? 'bg-[#971253] border-[#971253] shadow-[0_10px_30px_-10px_rgba(151,18,83,0.5)]' 
        : savings 
          ? 'bg-[#FFF5EE] border-[#528a89]/30 hover:border-[#528a89] shadow-sm'
          : 'bg-white/40 border-[#971253]/10 hover:border-[#971253]/30 shadow-sm'
      }
    `}>
      {/* Background Decorative Accent for Premium */}
      {isPremium && (
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
      )}

      {/* Savings Badge */}
      {savings && (
        <div className={`
          absolute top-0 left-1/2 -translate-x-1/2 px-4 py-1 rounded-b-lg text-[10px] font-bold uppercase tracking-widest whitespace-nowrap
          ${isPremium ? 'bg-[#528a89] text-[#FFF5EE]' : 'bg-[#971253]/10 text-[#971253]'}
        `}>
          {savings}
        </div>
      )}

      <h4 className={`font-serif text-xl mt-5 mb-1 ${isPremium ? 'text-[#FFF5EE]' : 'text-[#971253]'}`}>
        {duration} {duration === "1" || duration === 1 ? "Mês" : "Meses"}
      </h4>
      
      <div className="flex flex-col items-center justify-center my-3 flex-grow w-full">
          {/* Preço em Destaque (Alinhado pelo Topo) */}
          <div className={`flex items-start justify-center ${isPremium ? 'text-white' : 'text-stone-800'}`}>
            <span className="text-sm font-medium mr-1 mt-1.5">R$</span>
            <span className="text-5xl font-serif font-bold tracking-tight leading-none">{integerPart}</span>
            <span className="text-2xl font-serif opacity-80 mt-0.8">,{centsPart}</span>
          </div>
          <span className={`text-[11px] font-bold uppercase tracking-wider mt-2 ${isPremium ? 'text-white/80' : 'text-stone-500'}`}>
            por pessoa / mês
          </span>

          {/* Badge do Valor Total da Fatura */}
          <div className={`text-[11px] mt-4 px-3 py-1.5 rounded-full ${isPremium ? 'bg-black/20 text-white/90' : 'bg-stone-100 text-stone-600'}`}>
            {duration === "1" || duration === 1 ? `Total de R$ ${totalPrice}` : `Total de R$ ${totalPrice} por ${duration} meses`}
          </div>

          {/* Bônus de Desconto no Bistrô */}
          {hasDiscount && (
            <div className={`text-xs font-bold mt-3 px-3 py-1.5 rounded-lg border border-dashed w-full ${isPremium ? 'border-[#dbcdac]/50 text-[#dbcdac] bg-[#dbcdac]/10' : 'border-[#528a89]/50 text-[#528a89] bg-[#528a89]/5'}`}>
              🎁 + {discountPercent}% OFF nas Compras
            </div>
          )}
      </div>

      <button className={`
        mt-4 w-full py-3 rounded-xl font-medium text-sm tracking-wide transition-all
        ${isPremium 
          ? 'bg-[#FFF5EE] text-[#971253] hover:bg-white' 
          : 'bg-[#971253]/10 text-[#971253] hover:bg-[#971253] hover:text-[#FFF5EE]'
        }
      `}>
        Selecionar
      </button>
    </div>
  );
};

// --- Main Page Component ---

const SubscriptionPage = () => {
  const navigate = useNavigate();

  const handleSubscriptionClick = (pessoas, duracao) => {
    navigate("/assinatura", {
      state: {
        prefillData: { numPessoas: pessoas, duracao: duracao }
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#FFF5EE] text-stone-800 font-sans selection:bg-[#971253] selection:text-white overflow-x-hidden">

      {/* 1. HERO SECTION */}
      <section className="relative h-screen flex flex-col items-center justify-center px-6 text-center bg-[#528a89] overflow-hidden">
        
        <div className="absolute top-0 left-0 w-64 h-64 border-r border-b border-[#FFF5EE]/20 rounded-br-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border-l border-t border-[#FFF5EE]/20 rounded-tl-[100px] pointer-events-none" />
        
        <div className="max-w-4xl z-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
          
          <span className="flex items-center justify-center gap-2 text-[#FFF5EE] uppercase tracking-[0.2em] text-sm font-medium mb-8">
            <Crown size={16} /> Clube Deli da Persy
          </span>
          
          <h1 className="font-serif text-6xl md:text-8xl text-[#FFF5EE] leading-[1.1] mb-10 [text-shadow:4px_4px_0px_#234e4d]">
            Jogue Mais. <br/> 
            <span className="italic text-5xl md:text-7xl opacity-90 block mt-2">
              Viva Mais Histórias.
            </span>
          </h1>
          
          <p className="max-w-lg mx-auto text-lg text-[#FFF5EE] leading-relaxed mb-12 font-light drop-shadow-md">
            Desbloqueie o acesso ilimitado ao nosso acervo, vantagens exclusivas no bistrô e prioridade nas mesas. Sua campanha épica começa aqui.
          </p>

          <button onClick={() => document.getElementById('planos').scrollIntoView({ behavior: 'smooth' })} className="group relative px-10 py-5 bg-[#971253] text-[#FFF5EE] rounded-full overflow-hidden shadow-[0_10px_20px_-5px_rgba(0,0,0,0.3)] hover:shadow-xl hover:shadow-[#971253]/40 transition-all border border-[#FFF5EE]/30">
            <span className="relative z-10 flex items-center font-medium tracking-wide text-lg">
              Assinar Agora <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-500" />
            </span>
            <div className="absolute inset-0 bg-[#7a0e42] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-out" />
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto block align-middle">
              <path fill="#FFF5EE" fillOpacity="1" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
           </svg>
        </div>
      </section>

      {/* 2. EXPLANATION SECTION */}
      <section className="py-24 px-6 max-w-4xl mx-auto text-center">
        <h2 className="font-serif text-4xl md:text-5xl text-[#971253] mb-8">O Passaporte para a Fantasia</h2>
        <p className="text-xl text-stone-600 leading-relaxed font-light">
          Ao se tornar um membro do clube, você deixa de pagar o passaporte diário e ganha a liberdade de visitar a <span className="text-[#528a89] font-medium">Deli da Persy</span> sempre que quiser. Experimente novos jogos, traga sua guilda, e desfrute de uma comunidade que compartilha a mesma paixão.
        </p>
      </section>

      {/* 3. PRICING MATRIX */}
      <section id="planos" className="py-16 px-6 bg-white border-y border-[#971253]/10">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#971253] mb-4">Escolha seu Grupo</h2>
            <p className="text-stone-500">Quanto maior o plano e o grupo, maiores as vantagens e a economia.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            
            {/* COLUMN 1: 1 Person */}
            <div className="bg-[#FFF5EE]/50 p-6 rounded-3xl border border-[#971253]/10 flex flex-col">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#528a89]/10 text-[#528a89] mb-4">
                  <User strokeWidth={1.5} size={32} />
                </div>
                <h3 className="font-serif text-3xl text-[#528a89]">Aventureiro Solo</h3>
                <p className="text-stone-500 text-sm mt-2">Acesso para 1 Pessoa</p>
              </div>
              <div className="space-y-4 flex-grow flex flex-col">
                <PricingCard duration="1" totalPrice="88,00" monthlyPerPerson="88,00" onClick={() => handleSubscriptionClick(1, 1)} />
                <PricingCard duration="6" totalPrice="480,00" monthlyPerPerson="80,00" hasDiscount={true} onClick={() => handleSubscriptionClick(1, 6)} />
                <PricingCard duration="12" totalPrice="864,00" monthlyPerPerson="72,00" isPremium={true} hasDiscount={true} onClick={() => handleSubscriptionClick(1, 12)} />
              </div>
            </div>

            {/* COLUMN 2: 2 People */}
            <div className="bg-[#FFF5EE]/50 p-6 rounded-3xl border border-[#971253]/10 relative flex flex-col">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#528a89]/10 text-[#528a89] mb-4 relative">
                  <Users strokeWidth={1.5} size={32}/>
                </div>
                <h3 className="font-serif text-3xl text-[#528a89]">Dupla Dinâmica</h3>
                <p className="text-stone-500 text-sm mt-2">Acesso para 2 Pessoas</p>
              </div>
              <div className="space-y-4 flex-grow flex flex-col">
                <PricingCard duration="1" totalPrice="158,00" monthlyPerPerson="79,00" savings="Vantagem de Grupo" onClick={() => handleSubscriptionClick(2, 1)} />
                <PricingCard duration="6" totalPrice="852,00" monthlyPerPerson="71,00" savings="Vantagem de Grupo" hasDiscount={true} onClick={() => handleSubscriptionClick(2, 6)} />
                <PricingCard duration="12" totalPrice="1.488,00" monthlyPerPerson="62,00" isPremium={true} hasDiscount={true} onClick={() => handleSubscriptionClick(2, 12)} />
              </div>
            </div>

            {/* COLUMN 3: 4 People */}
            <div className="bg-[#971253]/5 p-6 rounded-3xl border border-[#971253]/20 shadow-[0_0_40px_-10px_rgba(151,18,83,0.1)] relative flex flex-col">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#FFF5EE] to-transparent rounded-3xl -z-10" />
              
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#971253]/10 text-[#971253] mb-4">
                  <Sword strokeWidth={1.5} size={32} />
                </div>
                <h3 className="font-serif text-3xl text-[#971253]">Party Completa</h3>
                <p className="text-stone-500 text-sm mt-2">Acesso para 4 Pessoas</p>
              </div>
              <div className="space-y-4 flex-grow flex flex-col">
                <PricingCard duration="1" totalPrice="296,00" monthlyPerPerson="74,00" savings="Vantagem de Grupo" onClick={() => handleSubscriptionClick(4, 1)} />
                <PricingCard duration="6" totalPrice="1.488,00" monthlyPerPerson="62,00" savings="Vantagem de Grupo" hasDiscount={true} onClick={() => handleSubscriptionClick(4, 6)} />
                <PricingCard duration="12" totalPrice="2.688,00" monthlyPerPerson="56,00" savings="A Melhor Economia" isPremium={true} hasDiscount={true} onClick={() => handleSubscriptionClick(4, 12)} />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. BENEFITS SECTION */}
      <section className="py-24 px-6 bg-[#FFF5EE]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#528a89] mb-4">Vantagens do Clube</h2>
            <p className="text-stone-500">Muito além da isenção do passaporte.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Dices, title: "Acervo Ilimitado", desc: "Jogue qualquer um de nossos mais de 100 jogos sem custos adicionais." },
              { icon: CalendarCheck, title: "Prioridade VIP", desc: "Suas reservas têm preferência garantida, mesmo nos finais de semana lotados." },
              { icon: Ticket, title: "Eventos Exclusivos", desc: "Acesso antecipado a noites temáticas, lançamentos e eventos fechados." },
              { icon: PercentSquare, title: "Desconto Especial", desc: "Membros de planos semestrais e anuais recebem até 8% de desconto em todas as compras." },
            ].map((benefit, idx) => (
              <div key={idx} className="group p-8 bg-white/60 backdrop-blur-sm rounded-tr-[40px] rounded-bl-[40px] shadow-sm hover:shadow-md transition-all duration-300 border border-white/50 hover:border-[#528a89]/30">
                <benefit.icon className="w-10 h-10 text-[#528a89] mb-4 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                <h4 className="font-serif text-2xl text-[#971253] mb-2">{benefit.title}</h4>
                <p className="text-stone-600 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSeparator />

      {/* 5. FINAL CTA */}
      <section className="py-20 px-6 text-center relative overflow-hidden">
        <div className="max-w-2xl mx-auto relative z-10">
          <Sparkles className="mx-auto w-12 h-12 text-[#528a89] mb-6 opacity-80" />
          <h2 className="font-serif text-5xl md:text-7xl text-[#971253] mb-8">Comece sua Jornada</h2>
          <p className="text-stone-600 text-lg mb-10">
            A mesa está pronta. Os dados estão esperando. Escolha seu plano e junte-se ao conselho.
          </p>
          <button onClick={() => document.getElementById('planos').scrollIntoView({ behavior: 'smooth' })} className="px-10 py-5 bg-[#528a89] text-white rounded-2xl text-lg font-medium tracking-wide hover:bg-[#3f6d6c] hover:scale-105 transition-all shadow-lg hover:shadow-[#528a89]/30 border-b-4 border-[#3a6362] active:border-b-0 active:translate-y-1">
            Assinar Plano
          </button>
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#528a89]/10 rounded-full z-0 pointer-events-none" />
      </section>
      
    </div>
  );
};

export default SubscriptionPage;